/**
 * AdminLeadApprovalsPage — Admin queue for reviewing reassignment requests
 * submitted by Lead Managers.
 *
 * Apr 28, 2026 (Squad-D upgrade) — replaced Squad-B's table stub with the full
 * card-based queue from the Lead Manager design canvas (screen 12 of 12).
 *
 * Mounted at /admin/lead-approvals.
 *
 * For each pending request:
 *  - Lead identity, stake, lead aging, SLA remaining
 *  - From / To / Requested-by avatars
 *  - Reason
 *  - Optional comment
 *  - Approve / Reject buttons
 *
 * Backend:
 *  - GET  /lead-approval-requests?status=pending
 *  - POST /lead-approval-requests/:id/approve  body { comment? }
 *  - POST /lead-approval-requests/:id/reject   body { comment? }
 */

import { useEffect, useMemo, useState } from 'react';
import { Check, X } from 'lucide-react';
import { api } from '@/utils/api';
import { safeGet } from '@/pages/dashboard/roles/_dashboardShared';
import {
  PageShell,
  FieldLabel,
  Avatar,
  EmptyState,
  type ApprovalRequest,
  unwrapList,
  relativeTime,
} from '../lead-manager/_lmShared';

interface FullRequest extends ApprovalRequest {
  leadAgo?: string | null;
  stakeAmount?: string | null;
  slaText?: string | null;
}

export function AdminLeadApprovalsPage() {
  const [rows, setRows] = useState<FullRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comments, setComments] = useState<Record<string, string>>({});
  const [actingOn, setActingOn] = useState<string | null>(null);

  useEffect(() => {
    fetchPending();
  }, []);

  async function fetchPending() {
    setLoading(true);
    const r = await safeGet<unknown>('/lead-approval-requests?status=pending&limit=100');
    setRows(unwrapList<FullRequest>(r.data));
    setError(r.error);
    setLoading(false);
  }

  async function act(id: string, action: 'approve' | 'reject') {
    setActingOn(id);
    try {
      await api.post(`/lead-approval-requests/${id}/${action}`, {
        comment: comments[id] ?? undefined,
      });
      // Optimistic remove.
      setRows((prev) => prev.filter((r) => r.id !== id));
      setComments((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } catch (err) {
      alert(`${action} failed — backend not yet ready or permission denied.`);
    } finally {
      setActingOn(null);
    }
  }

  const pendingCount = rows.length;
  const overSla = useMemo(
    () =>
      rows.filter(
        (r) => r.slaRemainingMinutes != null && r.slaRemainingMinutes <= 0,
      ).length,
    [rows],
  );

  return (
    <PageShell
      title="Reassignment approvals"
      sub={
        loading
          ? 'Loading…'
          : `${pendingCount} pending${
              overSla > 0 ? ` · ${overSla} past SLA` : ''
            } · approve or reject with comment`
      }
      right={<button className="btn btn-outline">Bulk approve</button>}
    >
      {loading ? (
        <div style={{ padding: 28, color: 'var(--ink-3)', textAlign: 'center' }}>Loading…</div>
      ) : error ? (
        <EmptyState
          title="Approvals queue not available yet"
          sub="Backend endpoint isn't online. Once /lead-approval-requests ships, pending requests will appear here."
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Inbox clear"
          sub="No pending reassignment requests. Lead Managers' submissions land here."
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {rows.map((r) => {
            const sla =
              r.slaRemainingMinutes != null
                ? r.slaRemainingMinutes <= 0
                  ? 'SLA exceeded'
                  : `${Math.floor(r.slaRemainingMinutes / 60)}h ${r.slaRemainingMinutes % 60}m left`
                : r.slaText ?? null;
            const stake = r.stakeAmount ?? r.stake ?? null;
            return (
              <div key={r.id} className="card" style={{ padding: 18 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                        marginBottom: 6,
                        flexWrap: 'wrap',
                      }}
                    >
                      <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                        {r.id.slice(-6)}
                      </span>
                      <span className="chip chip-amber">Pending</span>
                      {sla && (
                        <span
                          style={{
                            fontSize: 11,
                            color:
                              sla === 'SLA exceeded' ? 'var(--danger)' : 'var(--warn)',
                            fontWeight: 500,
                          }}
                        >
                          {sla}
                        </span>
                      )}
                    </div>
                    <div
                      style={{
                        fontFamily: 'var(--ff-serif)',
                        fontSize: 18,
                        fontWeight: 500,
                      }}
                    >
                      {r.leadName ?? r.leadId ?? '—'}
                    </div>
                    {(stake || r.leadAgo) && (
                      <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                        {stake && (
                          <>
                            Stake:{' '}
                            <span style={{ color: 'var(--ink-1)', fontWeight: 500 }}>
                              {stake}
                            </span>
                          </>
                        )}
                        {stake && r.leadAgo && ' · '}
                        {r.leadAgo && <>in deal {r.leadAgo}</>}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <button
                      className="btn btn-outline"
                      onClick={() => act(r.id, 'reject')}
                      disabled={actingOn === r.id}
                    >
                      <X size={13} /> Reject
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => act(r.id, 'approve')}
                      disabled={actingOn === r.id}
                    >
                      <Check size={13} /> Approve
                    </button>
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1.5fr',
                    gap: 12,
                    marginTop: 14,
                    padding: 12,
                    background: 'var(--ivory-2)',
                    borderRadius: 8,
                  }}
                >
                  <div>
                    <FieldLabel>From</FieldLabel>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Avatar name={r.fromUserName ?? '?'} size={22} />
                      <span style={{ fontSize: 13 }}>
                        {r.fromUserName ?? '—'}
                        {r.fromTeamName && (
                          <span style={{ color: 'var(--ink-3)' }}> · {r.fromTeamName}</span>
                        )}
                      </span>
                    </div>
                  </div>
                  <div>
                    <FieldLabel>To</FieldLabel>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Avatar name={r.toUserName ?? '?'} size={22} />
                      <span style={{ fontSize: 13 }}>
                        {r.toUserName ?? '—'}
                        {r.toTeamName && (
                          <span style={{ color: 'var(--ink-3)' }}> · {r.toTeamName}</span>
                        )}
                      </span>
                    </div>
                  </div>
                  <div>
                    <FieldLabel>Requested</FieldLabel>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>
                        {r.createdAt ? relativeTime(r.createdAt) + ' ago' : '—'}
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 14 }}>
                  <FieldLabel>Reason given</FieldLabel>
                  <div
                    style={{
                      fontSize: 13,
                      color: 'var(--ink-1)',
                      padding: 10,
                      background: 'var(--ivory-0)',
                      border: '1px solid var(--line)',
                      borderRadius: 6,
                      lineHeight: 1.5,
                    }}
                  >
                    {r.reason ?? '—'}
                  </div>
                </div>

                <div style={{ marginTop: 12 }}>
                  <FieldLabel>Add a comment (optional)</FieldLabel>
                  <input
                    value={comments[r.id] ?? ''}
                    onChange={(e) =>
                      setComments((prev) => ({ ...prev, [r.id]: e.target.value }))
                    }
                    placeholder="e.g. Approved — confirm with new owner this week"
                    style={{
                      width: '100%',
                      height: 32,
                      padding: '0 10px',
                      border: '1px solid var(--line)',
                      borderRadius: 6,
                      fontFamily: 'inherit',
                      fontSize: 13,
                      background: 'var(--ivory-1)',
                      outline: 'none',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </PageShell>
  );
}

export default AdminLeadApprovalsPage;
